import { useState } from 'react';
import { Box, Typography, Button, Stack, TextField, FormControl, Select, MenuItem, InputLabel, CircularProgress, Alert, Snackbar } from '@mui/material';
import { styled } from '@mui/material/styles';
import { ArrowLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import Sidebar from '../components/dashboard/Sidebar';
import DashboardHeader from '../components/dashboard/DashboardHeader';
import { getFullUserData, getUserBusinessName } from '../utils/userHelper';
import { createTicket } from '../services/ticketService';
import type { TicketCategory, TicketPriority } from '../types/ticket';

const PageContainer = styled(Box)({
  display: 'flex',
  minHeight: '100vh',
  bgcolor: '#F9FAFB'
});

const MainContent = styled(Box)({
  flex: 1,
  display: 'flex',
  flexDirection: 'column'
});

const ContentArea = styled(Box)(({ theme }) => ({
  padding: theme.spacing(4),
  maxWidth: '800px',
  margin: '0 auto',
  width: '100%'
}));

const NewTicket = () => {
  const navigate = useNavigate();
  const userData = getFullUserData();
  const businessName = getUserBusinessName();
  
  const [subject, setSubject] = useState('');
  const [category, setCategory] = useState<TicketCategory | string>('');
  const [priority, setPriority] = useState<TicketPriority>('medium');
  const [description, setDescription] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const categories: { value: TicketCategory | string; label: string }[] = [
    { value: 'technical_issue', label: 'Technical Issue' },
    { value: 'general_question', label: 'General Question' },
    { value: 'integration', label: 'Integration' },
    { value: 'feature_request', label: 'Feature Request' },
    { value: 'billing', label: 'Billing' },
    { value: 'other', label: 'Other' }
  ];

  const priorities: { value: TicketPriority; label: string }[] = [
    { value: 'low', label: 'Low - General inquiry' },
    { value: 'medium', label: 'Medium - Need help soon' },
    { value: 'high', label: 'High - Business impacting' },
    { value: 'urgent', label: 'Urgent - Critical issue' }
  ];

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setSuccess(null);

    if (!subject.trim() || !category || !description.trim()) {
      setError('Please fill in all required fields');
      return;
    }

    setIsLoading(true);

    try {
      const response = await createTicket({
        subject: subject.trim(),
        category: category as TicketCategory,
        description: description.trim(),
        priority
      });

      console.log('Create ticket response:', response);

      // Handle different response structures
      // Check if response has success field or if it's a direct ticket object
      if (response && (response.success === true || response.success === undefined)) {
        // Try multiple possible response structures
        const ticket = 
          (response as any).data?.ticket || 
          (response as any).data || 
          (response as any).ticket ||
          response;

        // Check if we have a valid ticket with an ID
        if (ticket && (ticket.id || ticket.ticket_id)) {
          const ticketId = ticket.id || ticket.ticket_id;
          setSuccess('Ticket created successfully!');
          // Navigate to ticket details page with the new ticket ID
          navigate(`/help/ticket/${ticketId}`);
        } else if (response.success === true) {
          // Ticket was created but we can't find the ID in the response
          console.warn('Ticket created but ID not found in response:', response);
          setSuccess('Ticket created successfully!');
          // Navigate back to help center
          navigate('/help');
        } else {
          throw new Error((response as any).message || 'Failed to create ticket');
        }
      } else {
        throw new Error((response as any).message || 'Failed to create ticket');
      }
    } catch (err) {
      console.error('Error creating ticket:', err);
      setError(err instanceof Error ? err.message : 'Failed to create ticket. Please try again.');
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <PageContainer>
      <Sidebar user={userData} />
      
      <MainContent>
        <DashboardHeader
          businessName={businessName}
          userAvatar={userData.avatar}
          businessLogo={userData.businessLogo}
        />
        
        <ContentArea>
          {/* Back Button */}
          <Button
            startIcon={<ArrowLeft size={20} />}
            onClick={() => navigate('/help')}
            sx={{
              textTransform: 'none',
              color: '#6B7280',
              fontWeight: 600,
              fontSize: '0.875rem',
              mb: 3,
              '&:hover': {
                bgcolor: '#F3F4F6',
                color: '#111827'
              }
            }}
          >
            Back to Help Center
          </Button>

          {/* Form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={{
              bgcolor: 'white',
              borderRadius: '12px',
              p: 4,
              border: '1px solid #E5E7EB'
            }}
          >
            <Typography
              sx={{
                fontSize: '1.5rem',
                fontWeight: 700,
                color: '#111827',
                mb: 1
              }}
            >
              Create New Ticket
            </Typography>
            <Typography
              sx={{
                fontSize: '0.875rem',
                color: '#6B7280',
                mb: 4
              }}
            >
              Describe your issue and our support team will get back to you as soon as possible
            </Typography>

            <Stack spacing={3}>
              {/* Subject */}
              <Box>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#374151',
                    mb: 1
                  }}
                >
                  Subject <span style={{ color: '#EF4444' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  placeholder="Brief description of your issue"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': {
                        borderColor: '#E5E7EB'
                      },
                      '&:hover fieldset': {
                        borderColor: '#D1D5DB'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#4F46E5'
                      }
                    }
                  }}
                />
              </Box>

              {/* Category */}
              <Box>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#374151',
                    mb: 1
                  }}
                >
                  Category <span style={{ color: '#EF4444' }}>*</span>
                </Typography>
                <FormControl fullWidth required>
                  <Select
                    value={category}
                    onChange={(e) => setCategory(e.target.value)}
                    displayEmpty
                    sx={{
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#D1D5DB'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4F46E5'
                      }
                    }}
                  >
                    <MenuItem value="" disabled>
                      Select a category
                    </MenuItem>
                    {categories.map((cat) => (
                      <MenuItem key={cat.value} value={cat.value}>
                        {cat.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Priority */}
              <Box>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#374151',
                    mb: 1
                  }}
                >
                  Priority <span style={{ color: '#EF4444' }}>*</span>
                </Typography>
                <FormControl fullWidth required>
                  <Select
                    value={priority}
                    onChange={(e) => setPriority(e.target.value)}
                    sx={{
                      borderRadius: '8px',
                      '& .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#E5E7EB'
                      },
                      '&:hover .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#D1D5DB'
                      },
                      '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
                        borderColor: '#4F46E5'
                      }
                    }}
                  >
                    {priorities.map((p) => (
                      <MenuItem key={p.value} value={p.value}>
                        {p.label}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>
              </Box>

              {/* Description */}
              <Box>
                <Typography
                  sx={{
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    color: '#374151',
                    mb: 1
                  }}
                >
                  Description <span style={{ color: '#EF4444' }}>*</span>
                </Typography>
                <TextField
                  fullWidth
                  multiline
                  rows={6}
                  placeholder="Provide detailed information about your issue. Include any error messages, steps to reproduce, and what you've already tried."
                  value={description}
                  onChange={(e) => setDescription(e.target.value)}
                  required
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '8px',
                      '& fieldset': {
                        borderColor: '#E5E7EB'
                      },
                      '&:hover fieldset': {
                        borderColor: '#D1D5DB'
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: '#4F46E5'
                      }
                    }
                  }}
                />
                <Typography
                  sx={{
                    fontSize: '0.75rem',
                    color: '#9CA3AF',
                    mt: 1
                  }}
                >
                  Be as specific as possible. The more details you provide, the faster we can help.
                </Typography>
              </Box>

              {/* Submit Button */}
              {error && (
                <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
                  {error}
                </Alert>
              )}

              <Stack direction="row" spacing={2} sx={{ mt: 2 }}>
                <Button
                  type="submit"
                  variant="contained"
                  disabled={!subject || !category || !description || isLoading}
                  sx={{
                    bgcolor: '#4F46E5',
                    color: 'white',
                    textTransform: 'none',
                    px: 4,
                    py: 1.5,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    borderRadius: '8px',
                    boxShadow: 'none',
                    '&:hover': {
                      bgcolor: '#4338CA',
                      boxShadow: 'none'
                    },
                    '&:disabled': {
                      bgcolor: '#E5E7EB',
                      color: '#9CA3AF'
                    }
                  }}
                >
                  {isLoading ? (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                      <CircularProgress size={16} sx={{ color: 'white' }} />
                      Creating...
                    </Box>
                  ) : (
                    'Create Ticket'
                  )}
                </Button>
                <Button
                  variant="outlined"
                  onClick={() => navigate('/help')}
                  sx={{
                    textTransform: 'none',
                    color: '#6B7280',
                    borderColor: '#E5E7EB',
                    px: 4,
                    py: 1.5,
                    fontSize: '0.875rem',
                    fontWeight: 600,
                    borderRadius: '8px',
                    '&:hover': {
                      bgcolor: '#F9FAFB',
                      borderColor: '#D1D5DB'
                    }
                  }}
                >
                  Cancel
                </Button>
              </Stack>
            </Stack>
          </Box>
        </ContentArea>
      </MainContent>

      <Snackbar
        open={!!success}
        autoHideDuration={3000}
        onClose={() => setSuccess(null)}
        anchorOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <Alert onClose={() => setSuccess(null)} severity="success" sx={{ width: '100%' }}>
          {success}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default NewTicket;

